import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/profile/update-car-info/{uuid}' as const;
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type UpdateUnitInput = paths[url]['put'];

export interface UpdateCarInfoSuccessResponse {
    message: string;
}

export type UpdateCarInfoInput = {
    user_uuid: string;
    brand: string;
    model: string;
    color: string;
    plate: string;
    seats: number;
};

export const useCarInfoRequest = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);

    const request = async (vehicleUuid: string, props: UpdateCarInfoInput) => {
        setLoading(true);
        setError(null);
        setValidationErrors(null);
        setSuccess(null);

        try {

            const endpoint = ENDPOINT_URL.replace('{uuid}', vehicleUuid);
            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;
                const json = await response.json();
                error.message = json.message;
                error.errors = json.errors;
                error.statusCode = response.status;
                throw error;
            }

            const rawResponse = await response.json();
            setSuccess(rawResponse.message);
            return rawResponse.data;
        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
            err.errors && setValidationErrors(err.errors);
            throw err;
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return {
        request,
        loading,
        error,
        lastResponseTime,
        validationErrors,
        success,
    };
};
